import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ChangeEvent,
	type ReactNode,
} from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { fontFallback } from '@atlaskit/theme/typography';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import GenericError from '../../../common/assets/GenericError.svg';
import { CONTACT_ADMIN_LINK, OAUTH_UNSUCCESS_FLAG_ID } from '../../../common/constants';
import { AccessTokenModalType } from '../../../common/constants/access-token';
import {
	type SupportedThirdParty,
	SupportedThirdParties,
	type ImportType,
	CSV,
} from '../../../common/constants/import-type';
import { API_FLOW, type ImporterFlow, type ImporterSourceItem } from '../../../common/types';
import { ErrorScreen } from '../../../common/ui/error-screen';
import { SwitchBackToOldExperienceLink } from '../../../common/ui/old-experience-link';
import { SearchBar } from '../../../common/ui/search-bar';
import { SourceCardItem } from '../../../common/ui/source-card-item';
import { useContentForThirdParty } from '../../../common/utils';
import { useX2CFlag } from '../../../common/utils/use-flag';
import { useImportType, useSourceItems } from '../../../controllers/import-state/selectors';
import { useAccessTokenModalState } from '../../../controllers/ui-state';
import { useImporterSource } from '../../../services/importer-source';
import { useThirdPartyOAuth } from '../../../services/start-third-party-oauth-flow';
import { useThirdPartyAccessTokenStatus } from '../../../services/third-party-access-token-status';
import { EmptyView } from './empty-view';
import messages from './messages';
import { SourceGroup } from './source-group';

type Props = {
	onNext: (importType?: ImportType, supportedFlow?: ImporterFlow) => void;
	skipSourceSelection: boolean;
};

export const SourceSection = ({ onNext, skipSourceSelection }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const [importType] = useImportType();

	const [showFlag, setShowFlag] = useState(false);
	const [selectedThirdParty, setSelectedThirdParty] = useState<SupportedThirdParty | undefined>(
		undefined,
	);
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [isAuthLoading, setIsAuthLoading] = useState(false);

	const { isLoading, fetchImporterSource, hasErrorOccurred } = useImporterSource();
	const [sourceItems, { setSourceItems }] = useSourceItems();
	const { oAuthUnsuccessFlagTitle, oAuthUnsuccessFlagDescription } =
		useContentForThirdParty(selectedThirdParty);
	const { onShowFlag, onDismissFlag } = useX2CFlag();
	const startThirdPartyOAuth = useThirdPartyOAuth();
	const [, { setRequestAuthenticationToken }] = useAccessTokenModalState();
	const { validateAccessToken } = useThirdPartyAccessTokenStatus({
		requestAuthenticationToken: setRequestAuthenticationToken,
	});
	const isSearchEventSent = useRef(false);

	const onFetchImporterSource = useCallback(() => {
		fetchImporterSource().then((data) => {
			setSourceItems(data || []);
			// If skipSourceSelection and importType is already selected, then directly go to next step
			if (skipSourceSelection && importType) {
				const selectedItem = data?.find((item) => item.key === importType);
				selectedItem && onNext(selectedItem.key, selectedItem.supportedFlow);
			}
		});
	}, [fetchImporterSource, importType, onNext, setSourceItems, skipSourceSelection]);

	useEffect(() => {
		!sourceItems && onFetchImporterSource();
	}, [onFetchImporterSource, sourceItems]);

	useEffect(() => {
		if (showFlag) {
			setShowFlag(false);
			oAuthUnsuccessFlagTitle &&
				onShowFlag(OAUTH_UNSUCCESS_FLAG_ID, {
					title: oAuthUnsuccessFlagTitle,
					description: [
						oAuthUnsuccessFlagDescription,
						{
							link: (children: ReactNode) => (
								<a href={CONTACT_ADMIN_LINK} target="_blank">
									{children}
								</a>
							),
						},
					],
				});
		}
	}, [formatMessage, oAuthUnsuccessFlagDescription, oAuthUnsuccessFlagTitle, onShowFlag, showFlag]);

	const onAuthSuccess = useCallback(
		(selectedSource?: ImportType, supportedFlow?: ImporterFlow) => {
			isAuthLoading && setIsAuthLoading(false);
			onNext(selectedSource, supportedFlow);
		},
		[isAuthLoading, onNext],
	);

	const handleItemClick = useCallback(
		(item: ImporterSourceItem) => async () => {
			const analyticsEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'X2CSourceButton',
			});
			fireUIAnalytics(analyticsEvent, { importType: item.name });
			onDismissFlag(OAUTH_UNSUCCESS_FLAG_ID);
			if (item.supportedFlow === API_FLOW && SupportedThirdParties.has(item.key)) {
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				const selectedItem = item.key as SupportedThirdParty;
				selectedThirdParty !== selectedItem && setSelectedThirdParty(selectedItem);
				setIsAuthLoading(true);
				if (item.requiresOAuth) {
					startThirdPartyOAuth({
						thirdParty: selectedItem,
						onSuccess: (source) => onAuthSuccess(source, item.supportedFlow),
						onFail: () => {
							setShowFlag(true);
							setIsAuthLoading(false);
						},
					});
					return;
				}

				if (item.requiresPersonalAccessToken) {
					validateAccessToken(selectedItem).then((status) => {
						if (status === 'VALID') {
							onAuthSuccess(selectedItem, item.supportedFlow);
						} else {
							setRequestAuthenticationToken({
								thirdParty: selectedItem,
								modalType: AccessTokenModalType.ADD_NEW_TOKEN,
							}).then((isSaved) => {
								if (isSaved) {
									onAuthSuccess(selectedItem, item.supportedFlow);
								} else {
									setIsAuthLoading(false);
								}
							});
						}
					});
					return;
				}
			}
			onAuthSuccess(item.key, item.supportedFlow);
		},
		[
			createAnalyticsEvent,
			onAuthSuccess,
			onDismissFlag,
			selectedThirdParty,
			setRequestAuthenticationToken,
			startThirdPartyOAuth,
			validateAccessToken,
		],
	);

	const onSearchTermChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			setSearchTerm(e.target.value);
			if (!isSearchEventSent.current) {
				fireUIAnalytics(createAnalyticsEvent({}), 'importerSourceSearch searched');
				isSearchEventSent.current = true;
			}
		},
		[createAnalyticsEvent],
	);

	const filteredSourceItems = useMemo(
		() =>
			searchTerm === ''
				? sourceItems
				: sourceItems?.filter((item) =>
						item.name?.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()),
					),
		[searchTerm, sourceItems],
	);

	const sourceItemsForGroup = useMemo(() => {
		if (filteredSourceItems && filteredSourceItems.length > 0) {
			return filteredSourceItems;
		}

		return sourceItems?.filter((item) => item.name === CSV);
	}, [filteredSourceItems, sourceItems]);

	if (isLoading || skipSourceSelection) {
		return (
			<Box paddingBlockStart="space.400" xcss={contentWrapperStyles}>
				<Spinner
					size="large"
					testId="project-create-importer-upload-flow.ui.select-source-screen.source-section.spinner"
				/>
			</Box>
		);
	}

	if (hasErrorOccurred) {
		return (
			<Box xcss={errorScreenStyles}>
				<ErrorScreen
					testId="project-create-importer-upload-flow.ui.select-source-screen.source-section.error-screen"
					header={formatMessage(messages.errorHeading)}
					description={formatMessage(messages.errorDescription)}
					illustration={GenericError}
					onClick={onFetchImporterSource}
				/>
			</Box>
		);
	}

	return (
		<Box
			paddingBlockStart="space.400"
			testId="project-create-importer-upload-flow.ui.select-source-screen.source-section.wrapper"
		>
			<Box xcss={searchBarWrapperStyles}>
				<SearchBar
					onSearchTermChange={onSearchTermChange}
					placeholder={messages.searchPlaceholder}
				/>
			</Box>
			{filteredSourceItems?.length === 0 && <EmptyView />}
			{sourceItemsForGroup && (
				<SourceGroup
					sourceItems={sourceItemsForGroup}
					renderItem={(item: ImporterSourceItem) => (
						<SourceCardItem
							key={item.key}
							onClick={handleItemClick(item)}
							item={item}
							loading={selectedThirdParty === item.key && isAuthLoading}
							testId={`project-create-importer-upload-flow.ui.select-source-screen.source-section.source-card-item.${item.key}`}
						/>
					)}
				/>
			)}
			{searchTerm === '' && !ff('x2c-jim-home-screen-replacement_eikwe') && (
				<Box xcss={switchBackToOldExperienceStyles}>
					{formatMessage(messages.switchBackToOldExperience, {
						link: (content: ReactNode) => <SwitchBackToOldExperienceLink content={content} />,
					})}
				</Box>
			)}
		</Box>
	);
};

const errorScreenStyles = xcss({
	alignItems: 'center',
	justifyContent: 'center',
	display: 'flex',
	minHeight: '600px',
});

const contentWrapperStyles = xcss({
	alignItems: 'center',
	justifyContent: 'center',
	display: 'flex',
	minHeight: '200px',
});

const searchBarWrapperStyles = xcss({
	height: 'space.400',
	width: '240px',
	marginBottom: 'space.400',
});

const switchBackToOldExperienceStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	font: token('font.body', fontFallback.body.medium),
	color: 'color.text',
	paddingTop: 'space.100',
});
